'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Heading, MediaBlock, TextPassage } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import type { ReactElement } from 'react';
import { dataLayerPush } from '@libs/analytics';
import { ACTIVATE_OFFER_POST_LOGIN_ACTION, formatOfferDate, formatStoreEventSchedule, isStoreEventOffer, useLoyaltyContext, useOptimizely, usePageLayout } from '@libs/common-next';
import { isLocaleCA } from '@libs/core';
import { handleServiceClick } from '@libs/experience-api';
import { AlertBannerContainer } from '../ValidationAlert';
import { OfferCardButton } from './OfferCardButton';
import { useOfferCardActions } from './use-offer-card-actions';
export function OfferCardDetails() {
  const $ = _c(45);
  const {
    handleActivateOffer,
    offerPageSection,
    selectedOffer,
    showSpinner
  } = useOfferCardActions();
  const {
    isLoading
  } = useLoyaltyContext();
  const {
    isAuthenticated
  } = usePageLayout();
  const {
    context
  } = useOptimizely();
  const pathname = usePathname();
  if (!selectedOffer) {
    return null;
  }
  let t0;
  if ($[0] !== selectedOffer) {
    t0 = isStoreEventOffer(selectedOffer) ? formatStoreEventSchedule(selectedOffer.availabilityStart, selectedOffer.availabilityEnd) : "";
    $[0] = selectedOffer;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const storeEventSchedule = t0;
  let t1;
  if ($[2] !== selectedOffer.availabilityEnd || $[3] !== storeEventSchedule) {
    t1 = storeEventSchedule || `Exp ${formatOfferDate(selectedOffer.availabilityEnd)}`;
    $[2] = selectedOffer.availabilityEnd;
    $[3] = storeEventSchedule;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const dateStr = t1;
  const showButton = selectedOffer.isActivatable || selectedOffer.isActivated;
  let t2;
  if ($[5] !== selectedOffer.webUrl || $[6] !== selectedOffer.webUrlCa) {
    t2 = isLocaleCA() ? selectedOffer.webUrlCa : selectedOffer.webUrl;
    $[5] = selectedOffer.webUrl;
    $[6] = selectedOffer.webUrlCa;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shopButtonUrl = t2;
  let t3;
  if ($[8] !== offerPageSection || $[9] !== pathname) {
    t3 = offerPageSection ?? (pathname?.includes("offers") ? "offers" : "treats rewards");
    $[8] = offerPageSection;
    $[9] = pathname;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const siteSection = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AlertBannerContainer />;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== selectedOffer.shortDescription || $[13] !== selectedOffer.webImageUrl) {
    t5 = <MediaBlock.Header><img alt={selectedOffer.shortDescription} src={selectedOffer.webImageUrl} /></MediaBlock.Header>;
    $[12] = selectedOffer.shortDescription;
    $[13] = selectedOffer.webImageUrl;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== selectedOffer.shortDescription) {
    t6 = <Heading size="body-lg-bold" tagName="h2">{selectedOffer.shortDescription}</Heading>;
    $[15] = selectedOffer.shortDescription;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== dateStr) {
    t7 = <TextPassage size="sm"><p>{dateStr}</p></TextPassage>;
    $[17] = dateStr;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <MediaBlock.Body>{t6}{t7}</MediaBlock.Body>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t5 || $[23] !== t8) {
    t9 = <MediaBlock>{t5}{t8}</MediaBlock>;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== selectedOffer.longDescription) {
    t10 = <TextPassage className="offer-card-details-description"><p>{selectedOffer.longDescription}</p></TextPassage>;
    $[25] = selectedOffer.longDescription;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== context || $[28] !== handleActivateOffer || $[29] !== isLoading || $[30] !== selectedOffer || $[31] !== showButton || $[32] !== showSpinner || $[33] !== siteSection) {
    t11 = showButton && <OfferCardButton fullWidth={true} isActivated={selectedOffer.isActivated} loading={showSpinner(selectedOffer) || isLoading} onClick={e => {
      const element = e.currentTarget as HTMLElement;
      e.stopPropagation();
      context?.trackEvent("_optex__loyalty_fly_out_activation");
      handleActivateOffer(selectedOffer, siteSection, ACTIVATE_OFFER_POST_LOGIN_ACTION);
      element.blur();
    }} size="lg" />;
    $[27] = context;
    $[28] = handleActivateOffer;
    $[29] = isLoading;
    $[30] = selectedOffer;
    $[31] = showButton;
    $[32] = showSpinner;
    $[33] = siteSection;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== isAuthenticated || $[36] !== selectedOffer.shortDescription || $[37] !== shopButtonUrl || $[38] !== siteSection) {
    t12 = shopButtonUrl && <Button className="shop-offer-btn" text="Shop this offer" fullWidth={true} onClick={async event => {
      dataLayerPush({
        event: "click_to_shop_offer",
        selection: selectedOffer.shortDescription.toLowerCase(),
        page_section: siteSection
      });
      await handleServiceClick({
        authType: isAuthenticated,
        event,
        link: shopButtonUrl
      });
    }} />;
    $[35] = isAuthenticated;
    $[36] = selectedOffer.shortDescription;
    $[37] = shopButtonUrl;
    $[38] = siteSection;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t9) {
    t13 = <div className="c-offer-card-details">{t4}{t9}{t10}{t11}{t12}</div>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t9;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}